import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { BookOpen, LogIn, UserPlus, ArrowRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn, formatLibraryName } from "@/lib/utils";
import { useTenant } from "@/contexts/TenantContext";

const DEFAULT_LIBRARY_IMAGES = [
  "https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=1920&q=80",
  "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=1920&q=80",
];

export default function TenantLandingPage() {
  const { tenant } = useTenant();
  const tenantName = tenant?.name;
  const logoUrl = tenant?.logoUrl ?? null;
  const displayName = formatLibraryName(tenantName || "Library");

  const sliderImages = useMemo<string[]>(() => {
    const custom = (tenant?.authImages ?? (tenant?.settings as any)?.branding?.authImages) as
      | string[]
      | undefined;
    return custom && custom.length > 0 ? custom : DEFAULT_LIBRARY_IMAGES;
  }, [tenant?.authImages, tenant?.settings]);

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (sliderImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [sliderImages.length]);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ── Hero / Image Panel ───────────────────────────────── */}
      <div className="relative lg:w-[55%] min-h-[40vh] lg:min-h-screen overflow-hidden">
        {sliderImages.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden="true"
            className={cn(
              "absolute inset-0 w-full h-full object-cover transition-opacity duration-1000",
              i === currentSlide ? "opacity-100" : "opacity-0"
            )}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70 lg:bg-gradient-to-r lg:from-black/60 lg:via-black/30 lg:to-black/70" />

        {/* Logo + name overlay */}
        <div className="relative z-10 flex flex-col justify-between h-full p-8 lg:p-12">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={displayName}
                className="h-10 w-auto max-w-[200px] object-contain"
              />
            ) : (
              <>
                <BookOpen className="h-8 w-8 text-white" strokeWidth={2.5} />
                <span className="text-xl font-bold text-white tracking-tight">{displayName}</span>
              </>
            )}
          </div>

          {/* Welcome text — visible on large screens */}
          <div className="hidden lg:block max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-4">
              Welcome to {displayName}
            </h1>
            <p className="text-lg text-white/75 leading-relaxed">
              Access your library resources, manage your account, and explore our digital
              collection.
            </p>

            {/* Slide indicators */}
            {sliderImages.length > 1 && (
              <div className="flex items-center gap-1.5 mt-8" aria-hidden="true">
                {sliderImages.map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "h-1 rounded-full transition-all duration-500",
                      i === currentSlide ? "w-6 bg-white/80" : "w-1.5 bg-white/30"
                    )}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mobile: powered by */}
          <div className="lg:hidden" />
        </div>
      </div>

      {/* ── Content Panel ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col bg-[#F8F9FA]">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 sm:px-12 lg:px-16">
          <div className="w-full max-w-md space-y-8">
            {/* Mobile welcome */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Welcome to {displayName}
              </h1>
              <p className="text-sm text-gray-500">Sign in to your account or create a new one.</p>
            </div>

            {/* Desktop welcome */}
            <div className="hidden lg:block text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Get Started</h2>
              <p className="text-sm text-gray-500">
                Sign in to your account or register as a new patron.
              </p>
            </div>

            {/* Action Cards */}
            <div className="space-y-4">
              {/* Sign In Card */}
              <Link
                to="/login"
                className={cn(
                  "group flex items-center gap-5 p-5 sm:p-6 rounded-2xl border bg-white",
                  "shadow-sm hover:shadow-lg transition-all duration-300",
                  "hover:border-primary/30 hover:-translate-y-0.5"
                )}
              >
                <div className="shrink-0 p-3 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors">
                  <LogIn className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors">
                    Sign In
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Already have an account? Sign in to continue.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
              </Link>

              {/* Guest Catalog Card */}
              <Link
                to="/opac"
                className={cn(
                  "group flex items-center gap-5 p-5 sm:p-6 rounded-2xl border bg-white",
                  "shadow-sm hover:shadow-lg transition-all duration-300",
                  "hover:border-primary/30 hover:-translate-y-0.5"
                )}
              >
                <div className="shrink-0 p-3 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors">
                  <Search className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors">
                    Explore Catalog
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Browse and search the library collection without signing in.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
              </Link>

              {/* Register Card */}
              <Link
                to="/register"
                className={cn(
                  "group flex items-center gap-5 p-5 sm:p-6 rounded-2xl border bg-white",
                  "shadow-sm hover:shadow-lg transition-all duration-300",
                  "hover:border-primary/30 hover:-translate-y-0.5"
                )}
              >
                <div className="shrink-0 p-3 rounded-xl bg-primary/10 group-hover:bg-primary/15 transition-colors">
                  <UserPlus className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary transition-colors">
                    Register
                  </h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    New here? Create your patron account.
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
              </Link>
            </div>

            {/* Quick Sign In Button (primary CTA) */}
            <div className="pt-2">
              <Button asChild className="w-full h-12 text-base font-semibold rounded-xl shadow-md">
                <Link to="/login">
                  <LogIn className="h-5 w-5 mr-2" />
                  Sign In to {displayName}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-6 text-center">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} {displayName}. All rights reserved.
          </p>
          <p className="text-[10px] text-gray-300 mt-1">
            Powered by{" "}
            <a
              href="https://philolms.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-400 transition-colors"
            >
              PhiloLMS
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
